import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import LoginPage from './pages/LoginPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import ChangePasswordPage from './pages/ChangePasswordPage';
import AcceptInvitePage from './pages/AcceptInvitePage';
import DashboardLayout from './layouts/DashboardLayout';
import TodayPage from './pages/TodayPage';
import WaitlistPage from './pages/WaitlistPage';
import AppointmentsPage from './pages/AppointmentsPage';
import SlotActivationPage from './pages/SlotActivationPage';
import StatisticsPage from './pages/StatisticsPage';
import SettingsPage from './pages/SettingsPage';
import SettingsLayout from './pages/SettingsLayout';
import SettingsPraxisPage from './pages/SettingsPraxisPage';
import SettingsTeamPage from './pages/SettingsTeamPage';
import SettingsMessagesPage from './pages/SettingsMessagesPage';
import SettingsBillingPage from './pages/SettingsBillingPage';
import SettingsLegalPage from './pages/SettingsLegalPage';
import CustomersPage from './pages/CustomersPage';
import SuperadminRevenuePage from './pages/SuperadminRevenuePage';
import PublicWaitlistPage from './pages/public/PublicWaitlistPage';
import PublicLegalPage from './pages/public/PublicLegalPage';
import PublicBookingPage from './pages/public/PublicBookingPage';

function FullScreenLoader() {
  return <div className="flex h-screen items-center justify-center bg-background"><div className="text-text-secondary text-sm">…</div></div>;
}

function ProtectedRoute({ children, allowMustChangePassword = false }) {
  const { user, bootChecked } = useAuth();
  if (!bootChecked) return <FullScreenLoader />;
  if (!user || user === false) return <Navigate to="/login" replace />;
  // Force first-login password change — block every protected route until done.
  if (user.must_change_password === true && !allowMustChangePassword) {
    return <Navigate to="/passwort-aendern" replace />;
  }
  return children;
}

export default function App() {
  const { user, bootChecked } = useAuth();
  if (!bootChecked) return <FullScreenLoader />;

  return (
    <Routes>
      {/* Public routes — no auth */}
      <Route path="/warteliste/:slug" element={<PublicWaitlistPage />} />
      <Route path="/impressum/:slug" element={<PublicLegalPage field="impressum" titleKey="publicPage.footer.impressum" />} />
      <Route path="/datenschutz/:slug" element={<PublicLegalPage field="datenschutz" titleKey="publicPage.footer.datenschutz" />} />
      <Route path="/book/:token" element={<PublicBookingPage />} />

      {/* Auth */}
      <Route path="/login" element={user && user !== false ? <Navigate to="/heute" replace /> : <LoginPage />} />
      <Route path="/passwort-vergessen" element={<ForgotPasswordPage />} />
      <Route path="/passwort-neu-setzen" element={<ResetPasswordPage />} />
      <Route path="/passwort-aendern" element={
        <ProtectedRoute allowMustChangePassword>
          <ChangePasswordPage />
        </ProtectedRoute>
      } />
      <Route path="/einladung-annehmen" element={<AcceptInvitePage />} />

      {/* Protected */}
      <Route path="/" element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
        <Route index element={<Navigate to="/heute" replace />} />
        <Route path="heute" element={<TodayPage />} />
        <Route path="warteliste" element={<WaitlistPage />} />
        <Route path="termine" element={<AppointmentsPage />} />
        <Route path="slot-aktivierung" element={<SlotActivationPage />} />
        <Route path="statistik" element={<StatisticsPage />} />
        <Route path="einstellungen" element={<SettingsLayout />}>
          <Route index element={<Navigate to="/einstellungen/praxis" replace />} />
          <Route path="praxis" element={<SettingsPraxisPage />} />
          <Route path="team" element={<SettingsTeamPage />} />
          <Route path="nachrichten" element={<SettingsMessagesPage />} />
          <Route path="abo" element={<SettingsBillingPage />} />
          <Route path="rechtliches" element={<SettingsLegalPage />} />
        </Route>
        <Route path="kunden" element={<CustomersPage />} />
        <Route path="superadmin/revenue" element={<SuperadminRevenuePage />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
